import { createDailyTerrain, type Point } from './climbTerrain';
import { TREE_DESIGNS, DISCOVERIES } from './sceneryCatalog';

const path = (points: Point[]) => points.map((p,i)=>`${i ? 'L' : 'M'} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`).join(' ');
const slopeWidth = (model: ReturnType<typeof createDailyTerrain>, word: number, side: number) => (side < 0 ? 49 : 65) + 24*model.noise(word,110,side*419);

export function terrainEntrance(model: ReturnType<typeof createDailyTerrain>) {
  return { left: model.routePoint(0,-7).x - slopeWidth(model,0,-1), right: model.routePoint(0,7).x + slopeWidth(model,0,1) };
}

/** Sparse cartographic relief, anchored to absolute daily trail coordinates. */
export function contourTerrain(day: string, start: number, cardEdge = 125) {
  const model = createDailyTerrain(day);
  const first = Math.max(0, Math.floor(start/20)*20);
  const point = (word: number, side: number, cross: number): Point => {
    const ridge = model.routePoint(word, side*7);
    const width = slopeWidth(model,word,side);
    const shoulder = Math.sin(word/65 + side*2) * 6 * Math.sin(cross*Math.PI);
    const entrance = Math.min(1, Math.max(0, word / 45));
    const blend = entrance * entrance * (3 - 2 * entrance);
    return {x:ridge.x + side*(cross*width+shoulder), y:ridge.y + Math.pow(cross,1.3)*(38+model.noise(word,140,929)*19)*blend + 12*(1-blend)};
  };
  const marks = Array.from({length:181},(_,i)=>first+i*5);
  const contours = [-1,1].flatMap(side=>[.4,.72,1].map((cross,index)=>({
    id:`${side}:${index}`, outer:index===2, d:path(marks.map(word=>point(word,side,cross))),
  })));
  const right = marks.map(word=>point(word,1,1));
  const left = [...marks].reverse().map(word=>point(word,-1,1));
  const base = path(right) + ' ' + path(left).replace(/^M/,'L') + ' Z';
  // Fine color bands follow the existing slope, not the rectangular SVG bounds.
  const washes = [-1,1].flatMap(side=>Array.from({length:20},(_,i)=>{
    const inner=i/20, outer=(i+1)/20, cross=(inner+outer)/2;
    const blend=cross*cross*(3-2*cross);
    const crest=[253,250,243], meadow=[191,174,147];
    const rgb=crest.map((value,c)=>Math.round(value*(1-blend)+meadow[c]*blend));
    const d=path(marks.map(word=>point(word,side,inner)))+' '+path([...marks].reverse().map(word=>point(word,side,outer))).replace(/^M/,'L')+' Z';
    return {id:`${side}:${i}`,d,color:`rgb(${rgb.join(',')})`,opacity:.34-cross*.14};
  }));
  const hatches = [-1,1].flatMap(side=>Array.from({length:14},(_,i)=>{
    const cell = Math.floor(first/70)+i;
    const word = cell*70+20+model.noise(cell*70,41,827)*16;
    const points = Array.from({length:18},(_,j)=>{
      const cross=.13+j/17*.82;
      return point(word-cross*(12+model.noise(word,90,33)*10),side,cross);
    });
    return {id:`${side}:${cell}`, d:path(points)};
  }));
  const random = (cell: number, salt: number) => {
    let hash = model.seed ^ Math.imul(cell + salt, 374761393);
    hash = Math.imul(hash ^ (hash >>> 16), 2246822519);
    return (hash >>> 0) / 0x100000000;
  };
  // Reserve the entrance flag's full silhouette, including nearby tree crowns.
  const clearOfStart = (p: Point) => Math.abs(p.x - (model.x(0) + 75)) >= 48 || p.y < -115 || p.y > 65;
  const trees = Array.from({length:33},(_,i)=>{
    const cell=Math.floor(first/30)+i;
    const word=cell*30+random(cell,151)*20;
    const side=random(cell,153)<.5 ? -1 : 1;
    const meadow=side>0 && random(cell,155)<.12;
    const cross=meadow ? 1.1 : .63+random(cell,157)*.3;
    const p=point(word,side,cross);
    const routeAtY=model.x(Math.max(0,-p.y/2.5));
    return {...p,id:cell,variant:Math.floor(random(cell,159)*TREE_DESIGNS.length),scale:.7+random(cell,163)*.3,
      visible:random(cell,167)<.56 && (side>0 ? p.x-routeAtY>49 : routeAtY-p.x>35)};
  }).filter(tree=>tree.visible && clearOfStart(tree)).sort((a,b)=>a.y-b.y);
  // Independent optional stretches, with a full previous cell retained while scrolling.
  // Each stretch stays inside its cell so neighboring rivers cannot intersect.
  const riverFirstCell=Math.max(0,Math.floor(first/800)-1);
  const riverLastCell=Math.floor((first+900)/800);
  const rivers = Array.from({length:riverLastCell-riverFirstCell+1},(_,i)=>{
    const cell=riverFirstCell+i;
    if(random(cell,193)>.42) return null;
    const span=70+random(cell,201)*570;
    const word=cell*800+35+random(cell,197)*(730-span);
    const depth=20+random(cell,199)*72;
    const bends=1+Math.floor(random(cell,203)*5);
    const knots=[32,...Array.from({length:bends},(_,j)=>
      -depth*(.3+.7*random(cell,401+j*17))),32];
    const count=Math.ceil(span*2.5/5);
    const samples=Array.from({length:count+1},(_,j)=>{
      const t=j/count, position=t*(knots.length-1);
      const index=Math.min(knots.length-2,Math.floor(position));
      const blend=(1-Math.cos(Math.PI*(position-index)))/2;
      const desired=knots[index]*(1-blend)+knots[index+1]*blend;
      const atWord=word+t*span;
      // The slope extends downward from its word coordinate. Reserve its full
      // nearby outer edge plus the river bank, even on narrow cards.
      const boundary=Math.max(...[0,5,10,15,20,25,30].map(offset=>point(atWord+offset,1,1).x));
      return {x:Math.max(desired,boundary-cardEdge+20),y:-atWord*2.5};
    });
    return {id:cell,samples,d:path(samples)};
  }).filter(river=>river!==null);
  // Bank vegetation follows the actual generated curve, including its meanders.
  const bushes=rivers.flatMap(river=>{
    if(random(river.id,301)>.38) return [];
    return Array.from({length:random(river.id,303)<.2?2:1},(_,i)=>{
      const sample=river.samples[Math.floor((.2+random(river.id,305+i)*.6)*(river.samples.length-1))];
      return {id:`river:${river.id}:${i}`,x:sample.x+(random(river.id,309+i)<.5?-34:34),y:sample.y+3,
        variant:Math.floor(random(river.id,313+i)*3),scale:.65+random(river.id,317+i)*.2};
    });
  });
  for(let i=0;i<3;i++) {
    const cell=Math.floor(first/1000)+i;
    if(random(cell,331)>.06) continue;
    const y=-(cell*1000+60+random(cell,333)*700)*2.5;
    if(bushes.some(bush=>Math.abs(bush.y-y)<60)) continue;
    bushes.push({id:`meadow:${cell}`,x:-25-random(cell,335)*35,y,
      variant:Math.floor(random(cell,337)*3),scale:.6+random(cell,339)*.2});
  }
  // One weighted draw per 250-word cell: probability = cell size / mean interval.
  // Fixed cell coordinates make this independent of viewport and account.
  const curios = Array.from({length:6},(_,i)=>{
    const cell=Math.max(0,Math.floor(first/250)-1)+i;
    const draw=random(cell,227);
    let cumulative=0;
    const kind=DISCOVERIES.findIndex(item=>{cumulative+=250/item.meanWords;return draw<cumulative;});
    if(kind<0) return null;
    const word=cell*250+45+random(cell,211)*150;
    const side=random(cell,213)<.5 ? -1 : 1;
    const outside=side>0 && random(cell,215)<.18;
    const p=point(word,side,outside ? 1.1 : .86);
    const route=model.x(Math.max(0,-p.y/2.5));
    return {...p,x:outside ? Math.max(p.x,route+65) : p.x,id:cell,kind,side};
  }).filter((item):item is {x:number;y:number;id:number;kind:number;side:number}=>item!==null)
    .filter(item=>(item.side>0 || model.x(Math.max(0,-item.y/2.5))-item.x>35) && clearOfStart(item));
  const spacedTrees=trees.filter(tree=>!curios.some(item=>Math.abs(tree.y-item.y)<35 && Math.abs(tree.x-item.x)<30));
  return {base,contours,hatches,washes,trees:spacedTrees,rivers,bushes,curios};
}
